import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge, Row, Col, Spinner } from 'react-bootstrap';
import { FaRocket, FaEdit, FaDownload, FaTrash } from 'react-icons/fa';
import axios from 'axios';
import { API_URL } from '../config';

const DeployedTools = ({ onEditWorkflow, onCloneWorkflow }) => {
  const [deployedWorkflows, setDeployedWorkflows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDeployedWorkflows();
  }, []);

  const loadDeployedWorkflows = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/api/manual-workflows`);
      const workflowsData = response.data.workflows || {};
      const workflowsArray = Object.values(workflowsData);
      const deployed = workflowsArray.filter(w => w.deployed);
      setDeployedWorkflows(deployed);
    } catch (err) {
      console.error('Failed to load deployed workflows:', err);
      setError(`Failed to load deployed workflows: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUndeployWorkflow = async (workflowId) => {
    if (!window.confirm('Are you sure you want to undeploy this workflow? It will no longer be accessible.')) {
      return;
    }
    try {
      await axios.post(`${API_URL}/api/manual-workflows/${workflowId}/undeploy`);
      alert('Workflow undeployed successfully');
      loadDeployedWorkflows();
    } catch (err) {
      console.error('Failed to undeploy workflow:', err);
      alert(`Failed to undeploy workflow: ${err.message}`);
    }
  };

  return (
    <div className="container-fluid mt-4">
      <Card>
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <div>
            <h1 className="h4 mb-0"><FaRocket className="me-2" aria-hidden="true" />Deployed Tools</h1>
            <small>Access and manage your deployed workflow tools</small>
          </div>
          <Button 
            variant="outline-light" 
            size="sm"
            onClick={() => loadDeployedWorkflows()}
            disabled={isLoading}
          >
            {isLoading ? <Spinner animation="border" size="sm" /> : 'Refresh'}
          </Button>
        </Card.Header>

        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-3 text-muted">Loading deployed workflows...</p>
            </div>
          ) : deployedWorkflows.length === 0 ? (
            <Alert variant="info">
              <Alert.Heading>No Deployed Workflows</Alert.Heading>
              <p>You haven't deployed any workflows yet. Use the Workflow Creator to create and deploy a workflow.</p>
            </Alert>
          ) : (
            <Row>
              {deployedWorkflows.map((workflow) => (
                <Col key={workflow.id} md={6} lg={4} className="mb-3">
                  <Card className="h-100 shadow-sm">
                    <Card.Body>
                      <h2 className="h5 mb-3">{workflow.name}</h2>
                      {workflow.description && (
                        <p className="text-muted small mb-3">{workflow.description}</p>
                      )}
                      <div className="mb-3">
                        <Badge bg="primary" className="me-2">
                          {workflow.steps?.length || 0} {workflow.steps?.length === 1 ? 'agent' : 'agents'}
                        </Badge>
                        <Badge bg="success">
                          Deployed
                        </Badge>
                      </div>
                      {workflow.steps && workflow.steps.length > 1 && (
                        <div className="mb-3 small text-muted">
                          <strong>Workflow Chain:</strong>
                          <div className="mt-1">
                            {workflow.steps.map((step, idx) => (
                              <span key={idx}>
                                {step.agent?.name || 'Agent'}
                                {idx < workflow.steps.length - 1 && ' → '}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {workflow.steps && workflow.steps.length === 1 && (
                        <div className="mb-3 small text-muted">
                          <strong>Agent:</strong> {workflow.steps[0].agent?.name || 'Agent'}
                        </div>
                      )}
                      <div className="d-grid gap-2">
                        <Button 
                          variant="primary" 
                          href={`${API_URL}/api/tools/${workflow.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open ${workflow.name} (opens in a new tab)`}
                        >
                          <FaRocket className="me-2" aria-hidden="true" />
                          Open {workflow.name}
                        </Button>
                        <div className="d-flex gap-2">
                          {onEditWorkflow && (
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              className="flex-fill"
                              onClick={() => onEditWorkflow(workflow)}
                              title="Edit this workflow"
                            >
                              <FaEdit className="me-1" />
                              Edit
                            </Button>
                          )}
                          {onCloneWorkflow && (
                            <Button 
                              variant="outline-secondary" 
                              size="sm"
                              className="flex-fill"
                              onClick={() => onCloneWorkflow(workflow)}
                              title="Clone and modify"
                            >
                              <FaDownload className="me-1" />
                              Clone
                            </Button>
                          )}
                        </div>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleUndeployWorkflow(workflow.id)}
                        >
                          <FaTrash className="me-2" />
                          Undeploy
                        </Button>
                        <div className="mt-3 small text-muted">
                          <div><strong>URL:</strong></div>
                          <code className="d-block text-break">
                            {`${API_URL}/api/tools/${workflow.id}`}
                          </code>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default DeployedTools;
